import { IconButton, InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';
import { Clear, Search } from '@mui/icons-material';
import React, { useRef } from 'react';
import { useField } from 'formik';
import { useL10n } from '@uplift/hooks';

const getAdornment = (icon: React.ReactNode, position: 'start' | 'end') => {
  if (icon === false) {
    return null;
  }

  return (
    <InputAdornment position={position}>
      {icon}
    </InputAdornment>
  );
};

export type SearchBoxProps = BaseTextFieldProps & {
  name: string;
  clearable?: boolean;
  disableFormik?: boolean;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  inputClassName?: string;
  inputFullWidth?: boolean;
  placeholder?: string;
  rootClassName?: string;
  startIcon?: React.ReactNode | boolean;
  textFieldClassName?: string;
};

export const SearchBox = React.forwardRef<HTMLDivElement, SearchBoxProps>(({ disableFormik = false, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement>();
  const l10n = useL10n();
  const [ field, _meta, helpers ] = useField({
    name: props.name,
    type: 'text'
  });

  const formikObj = disableFormik
    ? {}
    : { ...field };

  const handleClear = () => {
    if (!disableFormik) {
      helpers.setValue(null);
    } else if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const { classes, cx } = useStyles(props, { props });
  const defaultStart = <Search fontSize="small" className={classes.icon} />;
  const clearIcon = (
    <IconButton size="small" onClick={handleClear}>
      <Clear fontSize="small" className={classes.icon} />
    </IconButton>
  );

  return (
    <div className={cx(classes.root, props.rootClassName)} ref={ref}>
      <BaseTextField
        {...props}
        label={l10n.onboarding.reason.visitReasons}
        className={cx(classes.textFieldRoot, props.textFieldClassName)}
        inputRef={inputRef}
        slotProps={{
          htmlInput: {
            ...props.inputProps,
            ...props.slotProps?.htmlInput,
          },
          input: {
            startAdornment: getAdornment(props.startIcon ?? defaultStart, 'start'),
            endAdornment: getAdornment(props.clearable ? clearIcon : props.endIcon, 'end'),
            classes: {
              input: classes.input,
              root: classes.inputRoot,
              notchedOutline: classes.noBorder,
            },
            className: props.inputClassName,
          }
        }}
        placeholder={props.placeholder}
        onChange={props.onChange}
        value={props.value}
        {...formikObj}
      />
    </div>
  );
});

type SearchBoxStyleProps = {
  fullWidth?: boolean;
  inputFullWidth?: boolean;
};

const useStyles = makeStyles<SearchBoxStyleProps>({ name: 'SearchBox' })((theme, { fullWidth, inputFullWidth }) => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    border: '1px solid #c4c4c4',
    borderRadius: '4px',
    boxShadow: 'inset 0 -1px 0 rgba(0, 0, 0, 0.12)',
    height: '64px',
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    width: fullWidth ? '100%' : undefined,
  },
  icon: {
    color: theme.palette.text.secondary,
  },
  input: {
    '&::placeholder': {
      color: theme.palette.text.hint,
      fontStyle: 'italic',
      letterSpacing: '0.15px',
      lineHeight: '20px',
    }
  },
  inputRoot: {
    backgroundColor: theme.palette.common.white,
    height: '48px',
  },
  mr: {
    marginRight: theme.spacing(1)
  },
  noBorder: {
    border: 'none',
    borderColor: 'none',
  },
  textFieldRoot: {
    width: inputFullWidth ? '100%' : '360px'
  }
}));
